/* eslint-disable camelcase */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Prompt } from 'react-router-dom';
import axios from 'axios';
import { Card, CardBody, FormGroup, Label, Input, Button } from 'reactstrap';
import { Switch, Button as JemButton } from '@jem-open/jem-ui';
import CollapsibleCard from '../../../components/CollapsibleCard';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import { environment } from '../../../environment/environment';
import toast from '../../../utils/toast';

const TIME_AND_ATTENDANCE = 'Time and Attendance';

const YOLA_EAP = 'Employee Assistance Programme';
const LYRA_EAP = 'EAP: Lyra';
// Only one EAP provider may be active per employer (enforced server-side in
// EmployerFeature.toggle_employer_feature). Reflected here so enabling one
// visually switches the other off before the change is saved.
const EAP_EXCLUSIVE_FEATURES = [YOLA_EAP, LYRA_EAP];

export interface EmployerFeature {
  id: number;
  name: string;
  enabled: boolean;
}

interface FeatureDef {
  name: string;
  depends: string[];
}

interface FeatureGroup {
  name: string;
  features: FeatureDef[];
}

interface EmployerFeaturesCardProps {
  employerId: string;
  features: EmployerFeature[];
  onSaveSuccess: () => Promise<void>;
}

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    name: 'Documents',
    features: [
      { name: 'Payslips', depends: [] },
      { name: 'Password Protected Payslips', depends: ['Payslips'] },
      { name: 'IRP5s', depends: [] },
      { name: 'Self Managed Payslips Matching Criteria', depends: [] },
    ],
  },
  {
    name: 'Earned Wage Access',
    features: [
      { name: 'Earned Wage Access', depends: [] },
      { name: 'Instant Payment', depends: ['Earned Wage Access'] },
      { name: 'Automated ODP', depends: ['Earned Wage Access'] },
    ],
  },
  {
    name: 'Financial Education',
    features: [{ name: 'Education', depends: [] }],
  },
  {
    name: 'Leave',
    features: [{ name: 'Leave Management', depends: [] }],
  },
  {
    name: 'Time & Attendance',
    features: [
      { name: TIME_AND_ATTENDANCE, depends: [] },
      { name: 'Timesheets', depends: [] },
      { name: 'Rosters', depends: [] },
    ],
  },
  {
    name: 'Segmentation',
    features: [{ name: 'Segments', depends: [] }],
  },
  {
    name: 'Benefits',
    features: [{ name: 'Rewards', depends: [] }],
  },
  {
    name: 'Jem Analytics',
    features: [
      { name: 'Dashboard', depends: [] },
      { name: 'Nudges', depends: ['Dashboard'] },
    ],
  },
];

const GROUPED_FEATURE_NAMES: string[] = FEATURE_GROUPS.flatMap((group) =>
  group.features.map((f) => f.name),
);

const RIGHT_COLUMN_GROUPS = ['Segmentation', 'Benefits'];

interface RewardsConfig {
  title: string;
  screen: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  successMsg: string | null;
  notConfigured: boolean;
}

const INITIAL_REWARDS_CONFIG: RewardsConfig = {
  title: '',
  screen: '',
  loading: false,
  saving: false,
  error: null,
  successMsg: null,
  notConfigured: false,
};

interface LyraConfig {
  code: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  successMsg: string | null;
  notAvailable: boolean;
}

const INITIAL_LYRA_CONFIG: LyraConfig = {
  code: '',
  loading: false,
  saving: false,
  error: null,
  successMsg: null,
  notAvailable: false,
};

export default function EmployerFeaturesCard({ employerId, features, onSaveSuccess }: EmployerFeaturesCardProps) {
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [rewardsConfig, setRewardsConfig] = useState<RewardsConfig>(INITIAL_REWARDS_CONFIG);
  const rewardsSuccessTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRewards = useRef<{ title: string; screen: string } | null>(null);
  const [lyraConfig, setLyraConfig] = useState<LyraConfig>(INITIAL_LYRA_CONFIG);
  const lyraSuccessTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedLyra = useRef<{ code: string } | null>(null);

  const rewardsServerEnabled = features.some((f) => f.name === 'Rewards' && f.enabled);
  const lyraServerEnabled = features.some((f) => f.name === LYRA_EAP && f.enabled);

  const loadRewardsConfig = useCallback(() => {
    setRewardsConfig((prev) => ({ ...prev, loading: true, error: null, notConfigured: false }));
    axios
      .get(`${environment.baseUrl}/employers/${employerId}/rewards_config`)
      .then((res) => {
        const title = res.data.title || '';
        const screen = res.data.screen || '';
        lastSavedRewards.current = { title, screen };
        setRewardsConfig((prev) => ({
          ...prev,
          loading: false,
          title,
          screen,
          notConfigured: false,
        }));
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setRewardsConfig((prev) => ({ ...prev, loading: false, notConfigured: true }));
        } else {
          setRewardsConfig((prev) => ({ ...prev, loading: false, error: 'Failed to load Rewards config.' }));
        }
      });
  }, [employerId]);

  useEffect(() => {
    if (rewardsServerEnabled) {
      loadRewardsConfig();
    }
  }, [rewardsServerEnabled, loadRewardsConfig]);

  const loadLyraConfig = useCallback(() => {
    setLyraConfig((prev) => ({ ...prev, loading: true, error: null, notAvailable: false }));
    axios
      .get(`${environment.baseUrl}/api/employer_features_config/lyra/?employer_id=${employerId}`)
      .then((res) => {
        const serverCode = res.data.code || '';
        // Track the server value so lyraDirty reflects divergence from what is
        // actually persisted (the code saves via its own button, not the toggle).
        lastSavedLyra.current = { code: serverCode };
        // Preserve a code the user has already typed (e.g. before enabling the
        // feature); only adopt the server value when the field is still empty.
        setLyraConfig((prev) => ({
          ...prev,
          loading: false,
          code: prev.code !== '' ? prev.code : serverCode,
          notAvailable: false,
        }));
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setLyraConfig((prev) => ({ ...prev, loading: false, notAvailable: true }));
        } else {
          setLyraConfig((prev) => ({ ...prev, loading: false, error: 'Failed to load Lyra config.' }));
        }
      });
  }, [employerId]);

  // Reset the Lyra panel when switching employers so one employer's code never
  // leaks into another's panel (EmployerFeaturesCard is not remounted per employer).
  useEffect(() => {
    lastSavedLyra.current = null;
    setLyraConfig(INITIAL_LYRA_CONFIG);
  }, [employerId]);

  useEffect(() => {
    if (lyraServerEnabled) {
      loadLyraConfig();
    } else {
      // Lyra is off: clear any stale code/dirty state so the unsaved-changes
      // Prompt does not keep firing for a panel that is no longer shown.
      lastSavedLyra.current = null;
      setLyraConfig(INITIAL_LYRA_CONFIG);
    }
  }, [lyraServerEnabled, loadLyraConfig]);

  useEffect(() => () => {
    if (rewardsSuccessTimer.current) {
      clearTimeout(rewardsSuccessTimer.current);
    }
    if (lyraSuccessTimer.current) {
      clearTimeout(lyraSuccessTimer.current);
    }
  }, []);

  const getEffectiveState = (feature: EmployerFeature): boolean => {
    const featureId = String(feature.id);
    return featureId in pendingChanges ? pendingChanges[featureId] : feature.enabled;
  };

  const handleToggle = (feature: EmployerFeature) => {
    setPendingChanges((prev) => {
      // Compute current effective state for all features
      const effective: Record<string, boolean> = Object.fromEntries(
        features.map((f) => [String(f.id), String(f.id) in prev ? prev[String(f.id)] : f.enabled]),
      );

      // Apply the toggle
      effective[String(feature.id)] = !effective[String(feature.id)];

      // Cascade: if a parent is now disabled, force its dependents off too
      FEATURE_GROUPS.forEach((group) => {
        group.features.forEach((featureDef) => {
          if (featureDef.depends.length > 0) {
            const parent = features.find((f) => f.name === featureDef.depends[0]);
            if (parent && !effective[String(parent.id)]) {
              const dependent = features.find((f) => f.name === featureDef.name);
              if (dependent) effective[String(dependent.id)] = false;
            }
          }
        });
      });

      // Mutual exclusivity: enabling one EAP provider switches the other off
      // (matches the server-side rule in EmployerFeature.toggle_employer_feature).
      if (EAP_EXCLUSIVE_FEATURES.includes(feature.name) && effective[String(feature.id)]) {
        features
          .filter((f) => EAP_EXCLUSIVE_FEATURES.includes(f.name) && f.id !== feature.id)
          .forEach((f) => {
            effective[String(f.id)] = false;
          });
      }

      // pendingChanges = only features whose effective state differs from server state
      return Object.fromEntries(
        features
          .filter((f) => effective[String(f.id)] !== f.enabled)
          .map((f) => [String(f.id), effective[String(f.id)]]),
      );
    });
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const rewardsDirty = lastSavedRewards.current !== null && (
    rewardsConfig.title !== lastSavedRewards.current.title ||
    rewardsConfig.screen !== lastSavedRewards.current.screen
  );
  const lyraDirty = lastSavedLyra.current !== null
    && lyraConfig.code !== lastSavedLyra.current.code;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${environment.baseUrl}/employer_features/batch_toggle/`, {
        employer_id: employerId,
        features: Object.entries(pendingChanges).map(([featureId, enabled]) => ({
          feature_id: Number(featureId),
          enabled,
        })),
      });
      await onSaveSuccess();
      setPendingChanges({});
      setSaving(false);
      toast.success('Features updated successfully');
    } catch (error) {
      setSaving(false);
      apiResponseHandler.handleApiError('Error saving features', error);
    }
  };

  const saveRewardsConfig = () => {
    const { title, screen } = rewardsConfig;
    const payload = {
      title: title.trim() === '' ? null : title.trim(),
      screen: screen.trim() === '' ? null : screen.trim(),
    };
    setRewardsConfig((prev) => ({ ...prev, saving: true, error: null, successMsg: null }));
    axios
      .patch(`${environment.baseUrl}/employers/${employerId}/rewards_config`, payload)
      .then((res) => {
        const savedTitle = res.data.title || '';
        const savedScreen = res.data.screen || '';
        lastSavedRewards.current = { title: savedTitle, screen: savedScreen };
        setRewardsConfig((prev) => ({
          ...prev,
          saving: false,
          successMsg: 'Saved.',
          title: savedTitle,
          screen: savedScreen,
        }));
        if (rewardsSuccessTimer.current) clearTimeout(rewardsSuccessTimer.current);
        rewardsSuccessTimer.current = setTimeout(() => {
          setRewardsConfig((prev) => ({ ...prev, successMsg: null }));
        }, 3000);
      })
      .catch((err) => {
        const data = err.response?.data;
        const detail = data?.detail ?? (data ? JSON.stringify(data) : undefined);
        setRewardsConfig((prev) => ({ ...prev, saving: false, error: detail || 'Save failed.' }));
      });
  };

  const renderRewardsConfig = () => {
    const { loading, saving: rewardsSaving, error, successMsg, notConfigured, title, screen } = rewardsConfig;

    if (notConfigured) {
      return (
        <p className="text-muted small mt-2">
          This employer has no white-label WhatsApp flow configured yet. Contact engineering to
          provision one before setting custom Rewards copy.
        </p>
      );
    }

    return (
      <Card className="mt-2 mb-2">
        <CardBody>
          <FormGroup>
            <Label>Rewards menu title</Label>
            <Input
              type="text"
              value={title}
              maxLength={60}
              placeholder="🎁 Your Rewards"
              disabled={loading || rewardsSaving}
              onChange={(e) => setRewardsConfig((prev) => ({ ...prev, title: e.target.value }))}
            />
            <small className="text-muted">Max 60 characters. Leave empty to use the default.</small>
          </FormGroup>
          <FormGroup>
            <Label>Rewards screen</Label>
            <Input
              type="text"
              value={screen}
              maxLength={80}
              placeholder="REWARDS_SCREEN"
              disabled={loading || rewardsSaving}
              onChange={(e) => setRewardsConfig((prev) => ({ ...prev, screen: e.target.value }))}
            />
            <small className="text-muted">Max 80 characters. Uppercase letters, digits, underscores. Leave empty to use the default.</small>
          </FormGroup>
          <Button
            color="primary"
            size="sm"
            disabled={loading || rewardsSaving}
            onClick={saveRewardsConfig}
          >
            {rewardsSaving ? 'Saving…' : 'Save'}
          </Button>
          {successMsg && <span className="ml-2 text-success small">{successMsg}</span>}
          {error && <span className="ml-2 text-danger small">{error}</span>}
        </CardBody>
      </Card>
    );
  };

  const saveLyraConfig = () => {
    const code = lyraConfig.code.trim();
    if (code === '') return; // backend rejects a blank code (allow_blank=False)
    setLyraConfig((prev) => ({ ...prev, saving: true, error: null, successMsg: null }));
    axios
      .put(`${environment.baseUrl}/api/employer_features_config/lyra/?employer_id=${employerId}`, { code })
      .then((res) => {
        const savedCode = res.data.code || '';
        lastSavedLyra.current = { code: savedCode };
        setLyraConfig((prev) => ({ ...prev, saving: false, successMsg: 'Saved.', code: savedCode }));
        if (lyraSuccessTimer.current) clearTimeout(lyraSuccessTimer.current);
        lyraSuccessTimer.current = setTimeout(() => {
          setLyraConfig((prev) => ({ ...prev, successMsg: null }));
        }, 3000);
      })
      .catch((err) => {
        const data = err.response?.data;
        const detail = data?.detail ?? (data ? JSON.stringify(data) : undefined);
        setLyraConfig((prev) => ({ ...prev, saving: false, error: detail || 'Save failed.' }));
      });
  };

  const renderLyraConfig = () => {
    const { loading, saving: lyraSaving, error, successMsg, notAvailable, code } = lyraConfig;

    if (notAvailable) {
      return (
        <p className="text-muted small mt-2">
          The EAP: Lyra feature is not provisioned for this employer yet. Enable and Save it first,
          then refresh to set the access code.
        </p>
      );
    }

    const trimmedEmpty = code.trim() === '';

    return (
      <Card className="mt-2 mb-2">
        <CardBody>
          <FormGroup>
            <Label>EAP: Lyra access code</Label>
            <Input
              type="text"
              value={code}
              maxLength={50}
              placeholder="JEM001"
              disabled={loading || lyraSaving}
              onChange={(e) => setLyraConfig((prev) => ({ ...prev, code: e.target.value }))}
            />
            <small className="text-muted">
              The unique Lyra code for this client, shown in the WhatsApp flow. Required, cannot be blank.
            </small>
          </FormGroup>
          <Button
            color="primary"
            size="sm"
            disabled={loading || lyraSaving || trimmedEmpty}
            onClick={saveLyraConfig}
          >
            {lyraSaving ? 'Saving…' : 'Save'}
          </Button>
          {successMsg && <span className="ml-2 text-success small">{successMsg}</span>}
          {error && <span className="ml-2 text-danger small">{error}</span>}
        </CardBody>
      </Card>
    );
  };

  const renderFeatureRow = (featureDef: FeatureDef) => {
    const feature = features.find((f) => f.name === featureDef.name);
    if (!feature) return null;
    const effectiveEnabled = getEffectiveState(feature);
    const parentFeature = featureDef.depends.length > 0
      ? features.find((f) => f.name === featureDef.depends[0])
      : null;
    const parentDisabled = parentFeature ? !getEffectiveState(parentFeature) : false;

    return (
      <div key={feature.id}>
        <div className="d-flex align-items-center justify-content-between py-1">
          <span style={{ fontSize: '14px' }}>{feature.name}</span>
          <Switch
            className="scale-75"
            checked={effectiveEnabled}
            onCheckedChange={() => handleToggle(feature)}
            disabled={parentDisabled || saving}
          />
        </div>
        {featureDef.name === 'Rewards' && effectiveEnabled && (
          <div className="mt-2">{renderRewardsConfig()}</div>
        )}
      </div>
    );
  };

  return (
    <>
      <Prompt
        when={hasPendingChanges || rewardsDirty || lyraDirty}
        message="You still have unsaved changes. Are you sure you want to leave?"
      />
      <CollapsibleCard title="Features" className="shadow mb-4" defaultCollapsed={false}>
            <CardBody>
              <div className="row">
                <div className="col-12 col-lg-6">
                  {FEATURE_GROUPS.filter((group) => !RIGHT_COLUMN_GROUPS.includes(group.name)).map((group) => (
                    <div key={group.name} className="mb-3">
                      <p className="font-weight-bold mb-1" style={{ fontSize: '14px' }}>{group.name}</p>
                      {group.features.map((featureDef) => renderFeatureRow(featureDef))}
                    </div>
                  ))}
                </div>
                <div className="col-12 col-lg-6">
                  {FEATURE_GROUPS.filter((group) => RIGHT_COLUMN_GROUPS.includes(group.name)).map((group) => (
                    <div key={group.name} className="mb-3">
                      <p className="font-weight-bold mb-1" style={{ fontSize: '14px' }}>{group.name}</p>
                      {group.features.map((featureDef) => renderFeatureRow(featureDef))}
                    </div>
                  ))}
                  {features && (
                    <div className="mb-3">
                      <p className="font-weight-bold mb-1" style={{ fontSize: '14px' }}>Other Features</p>
                      {features
                        .filter((f) => !GROUPED_FEATURE_NAMES.includes(f.name))
                        .map((feature) => (
                          <div key={feature.id}>
                            <div className="d-flex align-items-center justify-content-between py-1">
                              <span style={{ fontSize: '14px' }}>{feature.name}</span>
                              <Switch
                                className="scale-75"
                                checked={getEffectiveState(feature)}
                                onCheckedChange={() => handleToggle(feature)}
                                disabled={saving}
                              />
                            </div>
                            {feature.name === LYRA_EAP && getEffectiveState(feature) && (
                              <div className="mt-2">{renderLyraConfig()}</div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <JemButton
                  variant="primary"
                  disabled={!hasPendingChanges || saving}
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </JemButton>
              </div>
            </CardBody>
      </CollapsibleCard>
    </>
  );
}
